import {
  PatientPrivateOnboardRequest,
  PersonGeneral,
  PersonContact,
  MedicalAidValidationResponse,
  PatientPrivateOnboard,
  PartialPerson,
} from '@quromedical/models';
import { Col, Row } from 'components/base';
import { CrudForm, SubmissionHandler } from 'components/form';
import { Field } from 'components/types';
import { Button, Section, Visible, Text, Snackbar, Alert } from 'design-system';
import { FormikErrors } from 'formik';
import { ErrorMessageType, getErrorMessage, logger } from 'helpers';
import { getPlaceAddressPartialFetcher, googleSearchAddressFetcher } from 'integration';
import React, { useCallback, useState } from 'react';
import { View } from 'react-native';
import { JointFields, searchIDNumberSchema } from 'screens/patient';
import { strings } from 'strings';
import { trpc } from 'trpc';

import { Step } from '../internal';
import { ContactForm } from './ContactDetailsForm';
import { GeneralForm } from './GeneralForm';
import { NextOfKinForm } from './NextOfKinDetailsForm';

type PrivateDetails = {
  id: string;
  patientDetails: PartialPerson;
  nextOfkinDetails: Partial<JointFields>;
};

type FormStep =
  | Step<'search'>
  | Step<'searching'>
  | Step<'no-search-results'>
  | Step<'patient', PrivateDetails>
  | Step<'error', ErrorMessageType>
  | Step<'done'>;
export interface Search {
  id: string;
}
export interface SearchResult {
  search: Search;
  result: MedicalAidValidationResponse;
}
interface PrivateOnboardFormProps {
  onSubmit: (privatePatientOnBoard: PatientPrivateOnboardRequest) => Promise<void>;
  onUpdate: (privatePatientOnBoard: PatientPrivateOnboard) => Promise<void>;
}
interface FormState<TForm> {
  state: TForm;
  errors: FormikErrors<TForm>;
}

const createFields = (): Field<Search>[] => [
  {
    subfields: [
      {
        type: 'text-box',
        key: 'id',
        label: strings.LabelIDNumberOrMedicalAidNumber,
      },
    ],
  },
];

export const PrivateOnboardForm: React.FC<PrivateOnboardFormProps> = ({ onSubmit, onUpdate }) => {
  const [showErrors, setShowErrors] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<PatientPrivateOnboard>();
  const [_, setForm] = useState<Partial<Search>>({});
  const [formStep, setFormStep] = useState<FormStep>({
    key: 'search',
  });

  const handleSearchResult = useCallback((result: PrivateDetails) => {
    setFormStep({
      key: 'patient',
      data: result,
    });
  }, []);

  const [patientGeneral, setPatientGeneral] = useState<FormState<Partial<PersonGeneral>>>({
    state: {},
    errors: {},
  });

  const [patientContact, setPatientContact] = useState<FormState<Partial<PersonContact>>>({
    state: {},
    errors: {},
  });

  const [nextOfKin, setNextOfKin] = useState<FormState<Partial<JointFields>>>({
    state: {},
    errors: {},
  });

  const onPatientGeneralChange = useCallback(
    (data: Partial<PersonGeneral>, errors: FormikErrors<Partial<PersonGeneral>>) => {
      setPatientGeneral({ state: data, errors });
    },
    [setPatientGeneral]
  );

  const onPatientContactChange = useCallback(
    (data: Partial<PersonContact>, errors: FormikErrors<Partial<PersonContact>>) => {
      setPatientContact({ state: data, errors });
    },
    [setPatientContact]
  );

  const onNextOfKinChange = useCallback(
    (data: Partial<JointFields>, errors: FormikErrors<Partial<JointFields>>) => {
      setNextOfKin({ state: data, errors });
    },
    [setNextOfKin]
  );

  const handleSubmit = async () => {
    const generalErrorCount = Object.values(patientGeneral.errors).length;
    const contactErrorCount = Object.values(patientContact.errors).length;
    const nextOfKinErrorCount = Object.values(nextOfKin.errors).length;
    const hasErrors = generalErrorCount > 0 || contactErrorCount > 0 || nextOfKinErrorCount > 0;

    if (hasErrors) {
      setShowErrors(true);
      setHasError(false);
      return;
    }

    // data in form has been validated so we can cast
    const nextOfKinData = nextOfKin.state as JointFields;

    try {
      // on submit, we should have validated that all these fields are of the full required data
      await onSubmit({
        patient: {
          general: patientGeneral.state as PersonGeneral,
          contact: patientContact.state as PersonContact,
        },
        nextOfKin: {
          contact: nextOfKinData,
          general: nextOfKinData,
        },
      });
      setShowErrors(false);
    } catch (error) {
      setFormStep({
        key: 'error',
        data: getErrorMessage(error),
      });
      logger.error(error);
      setHasError(true);
      setShowAlert(true);
    }
  };

  const fields = createFields();

  const onSearch = useCallback<SubmissionHandler<Search>>(
    async (search: Search) => {
      try {
        const data = await trpc.client.patient.getPrivatePatient.query({ id: search.id });

        setSearchResults(data);
        handleSearchResult({
          id: data.id,
          patientDetails: { contact: data.contact, general: data.general },
          nextOfkinDetails: { ...data.nextOfKin?.contact, ...data.nextOfKin?.general },
        });

        return {};
      } catch (error) {
        setFormStep({
          key: 'error',
          data: getErrorMessage(error),
        });
        setShowAlert(true);

        return {};
      }
    },
    [handleSearchResult]
  );

  const hasPatientResults = formStep.key === 'patient';
  const patientGeneralData = hasPatientResults ? formStep.data.patientDetails.general : {};
  const patientContactData = hasPatientResults ? formStep.data.patientDetails.contact : {};
  const patientNextOfKinData = hasPatientResults ? formStep.data.nextOfkinDetails : {};

  const handleUpdate = useCallback(async () => {
    const generalErrorCount = Object.values(patientGeneral.errors).length;
    const contactErrorCount = Object.values(patientContact.errors).length;
    const nextOfKinErrorCount = Object.values(nextOfKin.errors).length;
    const hasErrors = generalErrorCount > 0 || contactErrorCount > 0 || nextOfKinErrorCount > 0;

    if (hasErrors) {
      setShowErrors(true);
      setHasError(false);
      return;
    }

    // data in form has been validated so we can cast
    const nextOfKinData = nextOfKin.state;
    try {
      await onUpdate({
        id: searchResults?.id as string,
        general: patientGeneral.state as PersonGeneral,
        contact: patientContact.state as PersonContact,
        nextOfKin: {
          id: searchResults?.nextOfKin?.id as string,
          contact: nextOfKinData,
          general: nextOfKinData,
          patient: searchResults?.nextOfKin?.patient,
          relationship: searchResults?.nextOfKin?.relationship,
        },
      });
      setShowErrors(false);
      setFormStep({
        key: 'done',
      });
    } catch (error) {
      setFormStep({
        key: 'error',
        data: getErrorMessage(error),
      });
      logger.error(error);
      setHasError(true);
      setShowAlert(true);
    }
  }, [
    patientGeneral.errors,
    patientGeneral.state,
    patientContact.errors,
    patientContact.state,
    nextOfKin.errors,
    nextOfKin.state,
    onUpdate,
    searchResults?.id,
    searchResults?.nextOfKin?.id,
    searchResults?.nextOfKin?.patient,
    searchResults?.nextOfKin?.relationship,
  ]);

  const onAlertClose = useCallback(() => {
    setShowAlert(false);
  }, []);

  const showError = formStep.key === 'error';
  const errorMessage = showError ? formStep.data : formStep.key;

  return (
    <>
      <CrudForm
        fields={fields}
        initialValues={{}}
        onChange={setForm}
        onChangeDebounceTime={0}
        onSubmit={onSearch}
        validationSchema={searchIDNumberSchema}
      />
      {formStep.key === 'patient' || formStep.key === 'error' ? (
        <Col>
          <Section>
            <GeneralForm
              key={`${JSON.stringify(patientGeneralData)}`}
              onChange={onPatientGeneralChange}
              showErrors={showErrors}
              initial={patientGeneralData}
            />
          </Section>
          <Section>
            <ContactForm
              key={`${JSON.stringify(patientContactData)}`}
              onChange={onPatientContactChange}
              showErrors={showErrors}
              addressFetcher={googleSearchAddressFetcher}
              getAddressPartial={getPlaceAddressPartialFetcher}
              initial={patientContactData}
            />
          </Section>
          <Section>
            <NextOfKinForm
              key={`${JSON.stringify(patientNextOfKinData)}`}
              onChange={onNextOfKinChange}
              showErrors={showErrors}
              addressFetcher={googleSearchAddressFetcher}
              getAddressPartial={getPlaceAddressPartialFetcher}
              initial={patientNextOfKinData}
            />
          </Section>
          <Section hasBottomMargin>
            <Row alignItems="flex-end" justifyContent="space-between">
              <Visible if={hasError}>
                <View>
                  <Text>{strings.LabelPrivatePatientOnboardError}</Text>
                </View>
              </Visible>
            </Row>
            <Row alignItems="flex-end" justifyContent="flex-end">
              <Visible if={formStep.key === 'error'}>
                <Button
                  onPress={handleSubmit}
                  text={strings.ButtonTextOnboardPatient}
                  icon="person-add-alt-1"
                  size="large"
                />
              </Visible>
              <Visible if={hasPatientResults}>
                <Button
                  onPress={handleUpdate}
                  text={strings.AdmissionButtonContinue}
                  size="large"
                />
              </Visible>
            </Row>

            <Snackbar isOpen={showAlert} onClose={onAlertClose}>
              <Alert
                actionIcon="close"
                backgroundColor="status-critical"
                onAction={onAlertClose}
                title={strings.AlertError}
                body={errorMessage as string}
                icon="error"
              />
            </Snackbar>
          </Section>
        </Col>
      ) : null}
    </>
  );
};
